import { IonButtons, IonContent, IonPage, useIonViewWillEnter } from '@ionic/react';
import { AppHeader, Button, Container } from '@mindly/ui-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { savedCreditCardsSelector } from '../../redux/savedCreditCards/selector';
import { SkeletonSavedCreditCards } from '../../components/SkeletonSavedCreditCards';
import { SavedCreditCards } from '../../components/SavedCreditCards';
import { StyledCardContainer, StyledCustomSkeleton, StyledProfileCardPageHeaderText } from './ProfileCardsPage.styled';
import { loadSavedCreditCards } from '../../redux/savedCreditCards/action';
import { userSelector } from '../../redux/user/selector';
import AddNewCard from '../../components/AddNewCard/AddNewCard';
import { environment } from '../../enviroments';
import { PublicRoutes } from '../../routes/navigation';
import { HttpHandler } from '../../api/HttpHandler';
import changeDefaultPaymentMethod from './utils';
import { FormattedMessage } from 'react-intl';
import { SavedCreditCardsType } from '../PaymentPage/type';
import useToast from '../../context/ToastContext/ToastContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripe = loadStripe(environment.STRIPE_PUBLIC_KEY);

const ProfileCardsPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const savedCreditCards = useSelector(savedCreditCardsSelector);
  const user = useSelector(userSelector);
  const [selectedCard, setSelectedCard] = useState<SavedCreditCardsType>({
    brand: '',
    exp_month: 0,
    exp_year: 0,
    last4: '',
    paymentMethodId: '',
    isDefaultPaymentMethod: false,
  });

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
  });

  const isFetchingSavedCreditCards = useMemo(() => savedCreditCards === null, [savedCreditCards]);

  useEffect(() => {
    user?.id && dispatch(loadSavedCreditCards(user?.id));
  }, [user?.id]);

  const handleDeleteCard = async (paymentMethodID: string) => {
    try {
      user?.id &&
        (await HttpHandler({
          method: 'POST',
          url: `${environment.PAYMENTS_URL}/customer/${user.id}/payment-methods/${paymentMethodID}/detach`,
        }));
      user?.id && dispatch(loadSavedCreditCards(user?.id));
      showToast({
        text: 'Карту успішно видалено',
      });
    } catch (error: any) {
      showToast({ error });
    }
  };

  const handleChangeDefaultPaymentMethod = async (paymentMethodID: string) => {
    const paymentMethod = savedCreditCards?.find((card) => card.paymentMethodId === paymentMethodID);
    try {
      if (paymentMethod && !paymentMethod.isDefaultPaymentMethod) {
        await changeDefaultPaymentMethod(paymentMethodID, user!.id!);
        user?.id && dispatch(loadSavedCreditCards(user?.id));
      }
      showToast({
        text: 'Метод оплати успішно змінений',
      });
    } catch (error) {
      showToast({
        error,
      });
    }
  };

  return (
    <IonPage>
      <AppHeader>
        <IonButtons slot="start">
          <Button buttonType="secondary" isDisabled={false} onClick={() => history.back()}>
            <FormattedMessage id={'general.back.btn'} defaultMessage={'Назад'} />
          </Button>
        </IonButtons>
      </AppHeader>
      <IonContent>
        <Container>
          {isFetchingSavedCreditCards ? (
            <div>
              <StyledCustomSkeleton heightInPx={32} marginBottomInPx={28} />
              <SkeletonSavedCreditCards skeletonsToDraw={2} />
            </div>
          ) : (
            !!savedCreditCards?.length && (
              <div>
                <StyledProfileCardPageHeaderText className={'bold'}>
                  <FormattedMessage id={'settings.card'} defaultMessage={'Мої картки'} />
                </StyledProfileCardPageHeaderText>
                <StyledCardContainer>
                  <SavedCreditCards
                    savedCreditCards={savedCreditCards}
                    setChoosedCreditCard={setSelectedCard}
                    choosedCreditCard={selectedCard}
                    handleDeleteCard={handleDeleteCard}
                    handleChangeDefaultPaymentMethod={handleChangeDefaultPaymentMethod}
                    isCardCanBeRemoved={true}
                  />
                </StyledCardContainer>
              </div>
            )
          )}
          <Elements stripe={stripe}>
            <AddNewCard user={user!} />
          </Elements>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default ProfileCardsPage;
